/**
 * useQueryParamState — URL-backed state for filter / search inputs.
 *
 * Operators type "billing" into a list page's filter, drill into one
 * row, hit back, and find the filter empty. They re-type. Every time.
 * The fix is mechanical: keep the input value in `?q=billing` so a
 * navigate-away-and-return restores it from the URL on mount.
 *
 * Generalizes the pattern that `useTestsSelection` and the admin
 * Audit / Users / Analytics pages already implement individually — one
 * `useSearchParams` call, replaceState write, default-strip semantics,
 * popstate sync. New list pages should reach for this hook rather than
 * rebuild it.
 *
 * Behavior pins:
 *   - **Read on mount** from `location.search` via `useSearchParams`.
 *   - **Write via `history.replaceState`** by default so a fresh URL
 *     change per keystroke doesn't pollute browser history with one
 *     entry per character. Set `history: 'push'` for the rare surface
 *     that wants Back to undo a filter change.
 *   - **Default-strip:** when `value === default`, the param is
 *     REMOVED from the URL — no `?q=` clutter on an empty filter.
 *     Equality uses `Object.is` semantics (strict ===); typed values
 *     need to round-trip through `parse`/`serialize` so default
 *     equality is a string-vs-string check at the URL boundary.
 *   - **popstate sync** is inherited from `useSearchParams` — when
 *     the user hits Back / Forward, the component re-renders against
 *     the new URL and `value` reflects the popped state.
 *
 * Typed values:
 *   Pass `parse: (raw: string | null) => T` and `serialize: (v: T) => string`
 *   to round-trip booleans, enums, or JSON. The default-strip check
 *   runs against the parsed value, so an enum default like `'all'`
 *   strips correctly even when the serialized form differs.
 *
 * Pairs naturally with #1213 (clearable Input): the X-clear button
 * calls `setValue('')` which auto-strips the URL param via the
 * default-strip behavior. No runtime coordination needed.
 */
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export interface UseQueryParamStateOptions<T> {
  /**
   * Parse the raw URL value into the typed state. Called with the
   * string from `searchParams.get(key)`, or `null` when the param is
   * absent. Should return the default when the input is null /
   * unparseable so a hand-edited URL gracefully falls back rather than
   * crashing the page.
   *
   * Default: identity-on-string (the raw value or the default when null).
   */
  parse?: (raw: string | null) => T
  /**
   * Serialize the typed state back to a URL string. Called whenever
   * the value diverges from the default and needs to land in the URL.
   *
   * Default: `String(value)`.
   */
  serialize?: (value: T) => string
  /**
   * History strategy for the write. Defaults to `'replace'` so
   * per-keystroke updates don't bloat the browser history stack.
   * Pass `'push'` for surfaces where each filter change should be
   * an independent Back step (rare — most list pages don't want
   * this).
   */
  history?: 'replace' | 'push'
}

/**
 * Generic URL-backed state hook for filter / search inputs. See file
 * header for the full contract.
 *
 * @example
 *   // Plain string filter — the common case.
 *   const [query, setQuery] = useQueryParamState('q', '')
 *
 * @example
 *   // Typed enum with default-strip.
 *   type Status = 'all' | 'active' | 'archived'
 *   const [status, setStatus] = useQueryParamState<Status>('status', 'all', {
 *     parse: (raw) => (raw === 'active' || raw === 'archived' ? raw : 'all'),
 *     serialize: (v) => v,
 *   })
 */
export function useQueryParamState<T = string>(
  key: string,
  defaultValue: T,
  options: UseQueryParamStateOptions<T> = {},
): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams()
  const parse = options.parse
  const serialize = options.serialize
  const historyMode = options.history ?? 'replace'

  // Read the typed value from the URL. `useMemo` keyed on the search
  // string so the value identity is stable across renders that don't
  // touch this param — important for downstream `useEffect` deps that
  // include the returned value.
  const searchKey = searchParams.toString()
  const value = useMemo(() => {
    const raw = searchParams.get(key)
    if (parse) return parse(raw)
    // Default behavior for the common `string` case: param absent →
    // defaultValue; param present → the raw string. The `as unknown as
    // T` is safe because the generic defaults to `string` when no
    // `parse` is supplied.
    return (raw ?? defaultValue) as unknown as T
    // `searchKey` covers `searchParams`; `parse` / `defaultValue` /
    // `key` are stable across the call site's lifetime in practice
    // (literal string keys, top-level defaults). Including them avoids
    // a stale value if the caller does swap them dynamically.
  }, [searchKey, key, parse, defaultValue])

  const setValue = useCallback(
    (next: T) => {
      setSearchParams(
        (prev) => {
          const out = new URLSearchParams(prev)
          // Default-strip: when the typed value is the default, the
          // param leaves the URL entirely so an empty filter doesn't
          // render as `?q=` clutter. `Object.is` covers NaN; for
          // strings / enums it's strict ===.
          if (Object.is(next, defaultValue)) {
            out.delete(key)
          } else {
            out.set(key, serialize ? serialize(next) : String(next))
          }
          return out
        },
        { replace: historyMode === 'replace' },
      )
    },
    [setSearchParams, key, defaultValue, serialize, historyMode],
  )

  return [value, setValue]
}
